import { useState } from "react";
import { FixaModal } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModal";
import { FixaSheets } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaSheets";
import { FixaModalHeader } from "../../../shared/wrappers/FixaModal/FixaModalHeader";
import { FixaModalFooter } from "../../../shared/wrappers/FixaModal/FixaModalFooter";
import { FixaInputField } from "../../../shared/wrappers/FixaInputField/FixaInputField";
import {
  FixaSelect,
  FixaOption,
} from "../../../shared/wrappers/FixaSelect/FixaSelect";
import FixaButton from "../../../shared/wrappers/FixaButton/FixaButton";
import {
  useGetAllPermissions,
  useCreateUserPermission,
} from "../../repositories/permissions.repository";
import { FixaModalBody } from "../../../shared/wrappers/FixaModal/FixaModalBody";
import { BasePermission } from "../../types/BasePermission";
import FixaCheckbox from "../../../shared/wrappers/FixaCheckbox/FixaCheckbox";
import FixaInlineMessage from "../../../shared/wrappers/FixaInlineMessage/FixaInlineMessage";

type Props = {
  isVisible: boolean;
  setIsOpen: (open: boolean) => void;
};

const EnableUserPermissionModal = ({ isVisible, setIsOpen }: Props) => {
  const [permissionId, setPermissionId] = useState("");
  const [uid, setUid] = useState("");
  const [value, setValue] = useState(true);

  const { data: permissions = [], isLoading: isLoadingPermissions } =
    useGetAllPermissions();
  const createUserPermissionMutation = useCreateUserPermission();

  const handleAddClick = async () => {
    try {
      await createUserPermissionMutation.mutateAsync({
        permission_id: permissionId,
        uid,
        value,
      });
      setPermissionId("");
      setUid("");
      setValue(true);
      setIsOpen(false);
    } catch {
      // createUserPermissionMutation.isError will show error message
    }
  };

  const handleClose = () => {
    setPermissionId("");
    setUid("");
    setValue(true);
    setIsOpen(false);
  };

  return (
    <FixaModal visible={isVisible} handleCloseBtn={handleClose}>
      <FixaSheets
        labelledById="feature-flags-enable-user-permissions-modal-header"
        alignment="right"
        preserveAlignment={false}
        size="small"
        fullSize
        header={
          <FixaModalHeader
            id="feature-flags-enable-user-permissions-modal-header"
            title={"Enable permission for user"}
            floating={false}
          />
        }
        footer={
          <FixaModalFooter>
            <FixaButton
              onClick={handleAddClick}
              disabled={!permissionId || !uid}
            >
              Add
            </FixaButton>
          </FixaModalFooter>
        }
      >
        <FixaModalBody>
          <FixaSelect
            id={"permissionIdSelect"}
            value={permissionId}
            onChange={(e) => setPermissionId(e.target.value)}
            label={"Permission code"}
            disabled={isLoadingPermissions}
          >
            <FixaOption value="" name="Select a permission" />
            {permissions.map((permission: BasePermission) => (
              <FixaOption
                key={permission.code}
                value={permission.code}
                name={`${permission.code} - ${permission.description}`}
              />
            ))}
          </FixaSelect>
          <FixaInputField
            id={"uidInput"}
            type={"text"}
            value={uid}
            onChange={(e) => setUid(e.target.value)}
            label={"User ID (uid)"}
          />
          <FixaCheckbox
            id="valueCheckbox"
            value="enabled"
            checked={value}
            onChange={() => setValue(!value)}
            label="Enabled"
          />
          {createUserPermissionMutation.isError && (
            <FixaInlineMessage
              variant="negative"
              title="Something went wrong. Please try again."
            />
          )}
        </FixaModalBody>
      </FixaSheets>
    </FixaModal>
  );
};

export default EnableUserPermissionModal;
